import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Search, Clock, BookOpen, ChevronRight, Grid, List, FileText, Filter, Atom, Zap, Waves, Thermometer, Gauge, Battery, Radio, Orbit, Activity, Lightbulb, Flame, Move, ArrowDown, Rocket, Scale, Magnet, Combine, Triangle, Droplet, Mountain, GitBranch, CircleDot, Target, Maximize2, Layers, Pause, Volume2, Sparkles, Minus, Droplets, Cloud, Minimize2, Calculator, Cpu, Factory, Radiation, TrendingUp, Wind } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import Navigation from '../../components/common/Navigation'
import Footer from '../../components/common/Footer'
import { lessonService, LessonItem } from '../../services/lesson/LessonService'

const BaiHoc: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const [lessons, setLessons] = useState<LessonItem[]>([])
  const [filteredLessons, setFilteredLessons] = useState<LessonItem[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedLop, setSelectedLop] = useState<string>(searchParams.get('lop') || 'all')
  const [selectedChuong, setSelectedChuong] = useState<string>(searchParams.get('chuong') || 'all')
  const [selectedBai, setSelectedBai] = useState<string>(searchParams.get('bai') || 'all')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')

  // Get appropriate icon for lesson based on exact lesson name (Brilliant.org style)
  const getLessonIcon = (lessonName: string) => {
    const iconMap: Record<string, any> = {
      // Grade 10 - Chapter 0
      'Giới thiệu mục đích học tập môn Vật lí': BookOpen,

      // Grade 10 - Chapter 1: Kinematics
      'Độ dịch chuyển và quãng đường đi được': Move,
      'Tốc độ và vận tốc - Chuyển động tổng hợp': Gauge,
      'Đồ thị độ dịch chuyển - thời gian': TrendingUp,
      'Chuyển động biến đổi': Activity,
      'Chuyển động rơi tự do': ArrowDown,
      'Chuyển động ném': Rocket,

      // Grade 10 - Chapter 2: Dynamics
      'Ba định luật Newton về chuyển động': Scale,
      'Một số lực trong thực tiễn': Magnet,
      'Tổng hợp lực và phân tích lực': Combine,
      'Cân bằng lực - Moment lực': Triangle,
      'Khối lượng riêng - Áp suất chất lỏng': Droplet,

      // Grade 10 - Chapter 3: Energy & Work
      'Công và năng lượng': Zap,
      'Động năng và thế năng': Mountain,
      'Công suất và hiệu suất': Gauge,
      'Động lượng - Định luật bảo toàn động lượng': GitBranch,
      'Động lượng và va chạm': CircleDot,

      // Grade 10 - Chapter 4: Circular Motion
      'Động học chuyển động tròn đều': Orbit,
      'Động học của chuyển động tròn đều': Orbit,
      'Gia tốc hướng tâm và lực hướng tâm': Target,
      'Sự biến dạng - Định luật Hooke': Maximize2,

      // Grade 11 - Chapter 1: Oscillations
      'Dao động điều hòa - Phương trình dao động điều hòa': Activity,
      'Vận tốc - Gia tốc trong dao động điều hòa': TrendingUp,
      'Năng lượng trong dao động điều hòa': Zap,
      'Dao động tắt dần - Dao động cưỡng bức - Hiện tượng cộng hưởng': Radio,

      // Grade 11 - Chapter 2: Waves
      'Mô tả sóng - Sự truyền sóng': Waves,
      'Các đặc trưng của sóng - Sóng dọc và sóng ngang': Waves,
      'Sóng điện từ': Radio,
      'Giao thoa sóng': Layers,
      'Sóng dừng': Pause,
      'Đo tốc độ truyền âm': Volume2,

      // Grade 11 - Chapter 3: Electric Field
      'Lực tương tác giữa các điện tích - Định luật Coulomb': Zap,
      'Khái niệm điện trường - Điện trường đều': Sparkles,
      'Điện thế và thế năng điện': Battery,
      'Điện dung và tụ điện - Năng lượng điện trường': Layers,

      // Grade 11 - Chapter 4: Electric Current
      'Dòng điện - Cường độ dòng điện': Zap,
      'Điện trở - Định luật Ohm': Minus,
      'Nguồn điện': Battery,
      'Năng lượng điện - Công suất điện': Lightbulb,

      // Grade 12 - Chapter 1: Thermodynamics
      'Sự chuyển thể': Droplets,
      'Nội năng - Định luật 1 của nhiệt động lực học': Flame,
      'Thang nhiệt độ - Nhiệt kế': Thermometer,
      'Nhiệt dung riêng': Flame,
      'Nhiệt nóng chảy riêng': Droplet,
      'Nhiệt hóa hơi riêng': Cloud,

      // Grade 12 - Chapter 2: Ideal Gas
      'Mô hình động học phân tử chất khí': Wind,
      'Định luật Boyle': Minimize2,
      'Định luật Charles': Thermometer,
      'Phương trình trạng thái của khí lí tưởng': Calculator,
      'Áp suất theo mô hình động học phân tử - Động năng phân tử': Gauge,

      // Grade 12 - Chapter 3: Magnetic Field
      'Từ trường': Magnet,
      'Lực từ tác dụng lên đoạn dây dẫn mang dòng điện - Cảm ứng từ': Orbit,
      'Từ thông - Hiện tượng cảm ứng điện từ': Zap,
      'Máy phát điện xoay chiều - Đại cương về dòng điện xoay chiều': Cpu,
      'Ứng dụng của hiện tượng cảm ứng điện từ': Lightbulb,
      'Điện từ trường - Mô hình sóng điện từ': Radio,

      // Grade 12 - Chapter 4: Nuclear Physics
      'Cấu trúc hạt nhân': Atom,
      'Độ hụt khối và năng lượng liên kết hạt nhân': Atom,
      'Sự phân hạch và tổng hợp hạt nhân': Sparkles,
      'Phóng xạ - An toàn phóng xạ': Radiation,
      'Công nghiệp hạt nhân': Factory
    }

    return iconMap[lessonName] || BookOpen
  }

  // Lấy danh sách bài học theo lớp
  const getBaiListByClass = (lop: string): number[] => {
    switch (lop) {
      case '10':
        return Array.from({ length: 20 }, (_, i) => i + 1)
      case '11':
        return Array.from({ length: 18 }, (_, i) => i + 1)
      case '12':
        return Array.from({ length: 22 }, (_, i) => i + 1)
      default:
        return []
    }
  }

  // Load lessons from LessonService (sử dụng cache)
  useEffect(() => {
    const loadLessons = async () => {
      setLoading(true)
      try {
        const lessonItems = await lessonService.getAllLessons()
        setLessons(lessonItems)
      } catch (error) {
        console.error('❌ Error loading lessons:', error)
      } finally {
        setLoading(false)
      }
    }
    loadLessons()
  }, [])

  // Filter lessons using LessonService
  useEffect(() => {
    const applyFilters = async () => {
      try {
        const filtered = await lessonService.filterLessons({
          lop: selectedLop !== 'all' ? parseInt(selectedLop) : undefined,
          chuong: selectedChuong !== 'all' ? parseInt(selectedChuong) : undefined,
          bai: selectedBai !== 'all' ? parseInt(selectedBai) : undefined,
          searchTerm: searchTerm || undefined
        })
        setFilteredLessons(filtered)
      } catch (error) {
        console.error('❌ Error filtering lessons:', error)
        setFilteredLessons([])
      }
    }
    applyFilters()
  }, [lessons, selectedLop, selectedChuong, selectedBai, searchTerm])

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams()
    if (selectedLop !== 'all') params.set('lop', selectedLop)
    if (selectedChuong !== 'all') params.set('chuong', selectedChuong)
    if (selectedBai !== 'all') params.set('bai', selectedBai)
    if (searchTerm) params.set('search', searchTerm)
    setSearchParams(params)
  }, [selectedLop, selectedChuong, selectedBai, searchTerm, setSearchParams])

  const handleOpenLesson = (lesson: LessonItem) => {
    navigate(lesson.lessonUrl)
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/50">
        <Navigation />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600 font-medium">Đang tải bài học...</p>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50/50">
      <Navigation />

      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="container mx-auto px-4 py-8"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="mb-12 text-center">
          <div className="inline-flex items-center justify-center p-4 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl shadow-lg mb-6 transform hover:scale-105 transition-transform duration-300">
            <BookOpen className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-5xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-4">
            Kho Tàng Kiến Thức
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Khám phá vật lí qua các bài học tương tác, mô phỏng trực quan và hệ thống bài tập đa dạng.
          </p>
        </motion.div>

        {/* Search and Filters */}
        <motion.div variants={itemVariants} className="glass-panel rounded-3xl p-8 mb-12 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-orange-100/50 to-transparent rounded-full blur-3xl -z-10" />

          <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-primary-100 rounded-lg">
                <Filter className="w-5 h-5 text-primary-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Bộ Lọc Tìm Kiếm</h2>
            </div>

            <div className="flex bg-slate-100 p-1 rounded-xl">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-all duration-200 ${viewMode === 'grid' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-all duration-200 ${viewMode === 'list' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Search */}
            <div className="md:col-span-12 lg:col-span-4">
              <div className="relative group">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                <input
                  type="text"
                  placeholder="Tìm kiếm bài học..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-white/50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                />
              </div>
            </div>

            {/* Filters */}
            <div className="md:col-span-4 lg:col-span-2">
              <select
                value={selectedLop}
                onChange={(e) => {
                  setSelectedLop(e.target.value)
                  setSelectedChuong('all')
                  setSelectedBai('all')
                }}
                className="w-full px-4 py-3 bg-white/50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none cursor-pointer"
              >
                <option value="all">Tất cả lớp</option>
                <option value="10">Lớp 10</option>
                <option value="11">Lớp 11</option>
                <option value="12">Lớp 12</option>
              </select>
            </div>

            <div className="md:col-span-4 lg:col-span-3">
              <select
                value={selectedChuong}
                onChange={(e) => {
                  setSelectedChuong(e.target.value)
                  setSelectedBai('all')
                }}
                className="w-full px-4 py-3 bg-white/50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none cursor-pointer disabled:opacity-50"
                disabled={selectedLop === 'all'}
              >
                <option value="all">Tất cả chương</option>
                <option value="1">Chương 1</option>
                <option value="2">Chương 2</option>
                <option value="3">Chương 3</option>
                <option value="4">Chương 4</option>
              </select>
            </div>

            <div className="md:col-span-4 lg:col-span-3">
              <select
                value={selectedBai}
                onChange={(e) => setSelectedBai(e.target.value)}
                className="w-full px-4 py-3 bg-white/50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none cursor-pointer disabled:opacity-50"
                disabled={selectedLop === 'all'}
              >
                <option value="all">Tất cả bài</option>
                {getBaiListByClass(selectedLop).map(bai => (
                  <option key={bai} value={bai}>Bài {bai}</option>
                ))}
              </select>
            </div>
          </div>
        </motion.div>

        {/* Results Info */}
        <motion.div variants={itemVariants} className="mb-8 flex items-center justify-between">
          <p className="text-gray-600 font-medium">
            Tìm thấy <span className="text-primary-600 font-bold text-lg">{filteredLessons.length}</span> bài học phù hợp
          </p>
        </motion.div>

        {/* Lesson Items */}
        {filteredLessons.length === 0 ? (
          <motion.div variants={itemVariants} className="text-center py-16 glass-panel rounded-3xl">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Search className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Không tìm thấy bài học nào</h3>
            <p className="text-gray-500">Hãy thử thay đổi bộ lọc hoặc từ khóa tìm kiếm của bạn</p>
          </motion.div>
        ) : (
          <div className="space-y-16">
            {/* Helper to render section */}
            {[10, 11, 12].map(grade => {
              const gradeLessons = filteredLessons.filter(l => l.lop === grade)
              if (gradeLessons.length === 0) return null

              const gradeColor = grade === 10 ? 'blue' : grade === 11 ? 'green' : 'indigo'
              const gradeGradient = grade === 10 ? 'from-blue-500 to-cyan-500' : grade === 11 ? 'from-green-500 to-emerald-500' : 'from-indigo-400 to-purple-400'

              return (
                <motion.div key={grade} variants={itemVariants}>
                  <div className="flex items-center mb-8">
                    <div className={`w-14 h-14 bg-gradient-to-br ${gradeGradient} rounded-2xl flex items-center justify-center mr-5 shadow-lg shadow-${gradeColor}-500/30`}>
                      <span className="text-2xl font-bold text-white">{grade}</span>
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-gray-800">Chương trình Lớp {grade}</h2>
                      <p className="text-gray-500 font-medium mt-1">{gradeLessons.length} bài học có sẵn</p>
                    </div>
                  </div>

                  <div className={viewMode === 'grid'
                    ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'
                    : 'space-y-4'
                  }>
                    {gradeLessons.map((lesson) => (
                      <motion.div
                        key={lesson.id}
                        whileHover={{ y: -8, scale: 1.02 }}
                        className="group glass-panel rounded-2xl overflow-hidden cursor-pointer border border-white/50 hover:border-white/80 transition-all duration-300"
                        onClick={() => handleOpenLesson(lesson)}
                      >
                        {/* Card Header */}
                        <div className={`p-4 bg-gradient-to-r ${gradeGradient} relative overflow-hidden min-h-[100px] flex flex-col justify-between`}>
                          <div className="absolute inset-0 bg-black/5 backdrop-blur-[1px]" />
                          <div className="relative z-10 flex items-start gap-3">
                            <div className="p-2 rounded-xl bg-white/20 backdrop-blur-md text-white border border-white/30 shrink-0">
                              {React.createElement(getLessonIcon(lesson.tenBai), { className: "w-5 h-5" })}
                            </div>
                            <h3 className="text-lg font-bold text-white leading-tight line-clamp-2 pt-1">
                              {lesson.tenBai}
                            </h3>
                          </div>
                          <div className="relative z-10 flex justify-end mt-2">
                            <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-md bg-white/20 backdrop-blur-md text-white border border-white/30 shadow-sm">
                              Lớp {grade}
                            </span>
                          </div>
                        </div>

                        {/* Card Content */}
                        <div className="p-5">

                          <p className="text-gray-600 mb-3 text-sm line-clamp-2 leading-normal">
                            {lesson.moTa}
                          </p>

                          {/* Metadata */}
                          <div className="flex items-center gap-3 text-xs text-gray-500 mb-4">
                            <div className="flex items-center bg-gray-50 px-2.5 py-1 rounded-lg">
                              <FileText className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                              <span className="font-medium">Lý thuyết</span>
                            </div>
                            <div className="flex items-center bg-gray-50 px-2.5 py-1 rounded-lg">
                              <Grid className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                              <span className="font-medium">Bài tập</span>
                            </div>
                          </div>

                          {/* Action */}
                          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                            <span className="text-sm font-semibold text-primary-600 group-hover:underline">
                              Bắt đầu học
                            </span>
                            <div className={`w-8 h-8 rounded-full bg-${gradeColor}-50 flex items-center justify-center group-hover:bg-${gradeColor}-100 transition-colors`}>
                              <ChevronRight className={`w-5 h-5 text-${gradeColor}-600`} />
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )
            })}
          </div>
        )}
      </motion.div>
      <Footer />
    </div>
  )
}

export default BaiHoc

